import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { MessageSquareQuote, Plus, Pencil, ArrowLeft, Mail, Eye } from "lucide-react";

const ViewContact = () => {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContact();
  }, [contactId]);

  const fetchContact = async () => {
    try {
      const response = await contactService.getContact(contactId);
      setContact(response.data.data);
    } catch (err) {
      Alert.error('Failed to load contact details');
      console.error(err);
      navigate('/contact');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading contact details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!contact) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                            <Mail className="w-7 h-7 text-buttonBg" />
                            Contact Messages
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600">View complete contact information</p>
                  </div>
                  <button
                                                            onClick={() => navigate('/contact')}
                                                            className="
                                                                  relative overflow-hidden
                                                                  group flex items-center justify-center gap-2
                                                                  px-3 sm:px-3 py-2.5 sm:py-2
                                                                  text-xs sm:text-sm
                                                                  rounded-lg
                                                                  border border-buttonBg
                                                                  bg-buttonBg text-buttonTxt
                                                                  font-medium whitespace-nowrap
                                    
                                                                  transition-colors duration-300 ease-out
                                                                  hover:bg-transparent hover:text-buttonBg
                                                                "
                                                              >
                                                                {/* COLLAPSING BACKGROUND */}
                                                                <span
                                                                  className="
                                                                    absolute inset-0
                                                                    bg-buttonBg
                                                                    scale-x-100
                                                                    group-hover:scale-x-0
                                                                    origin-center
                                                                    transition-transform duration-300 ease-out
                                                                    z-0
                                                                  "
                                                                />
                                    
                                                                {/* ICON */}
                                                                <ArrowLeft
                                                                  className="
                                                                    w-4 h-4 relative z-10
                                                                    transition-transform duration-300 ease-out
                                                                    group-hover:scale-90
                                                                  "
                                                                />
                                    
                                                                <span className="relative z-10">Back to Testimonials List</span>
                                                          </button>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 sm:p-6 md:p-8">
                    <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-sm border-2 border-gray bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl sm:text-3xl md:text-4xl">👤</span>
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <h2 className="text-xl sm:text-2xl md:text-2xl font-semibold text-tertiary mb-1 sm:mb-2 break-words">
                          {contact.full_name}
                        </h2>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <p className="text-sm sm:text-base md:text-lg text-orange-100 break-all">
                            {contact.email}
                          </p>

                          <span
                            className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold inline-block w-fit ${
                              contact.reply
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {contact.reply ? "Replied" : "Pending"}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Contact Information */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Contact ID</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-all border rounded-md bg-gray-50 p-4 py-3">{contact.id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Full Name</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-words border rounded-md bg-gray-50 p-4 py-3">{contact.full_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">First Name</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-md bg-gray-50 p-4 py-3">{contact.first_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Last Name</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-md bg-gray-50 p-4 py-3">{contact.last_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Email Address</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-all border rounded-md bg-gray-50 p-4 py-3">{contact.email}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Phone Number</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-md bg-gray-50 p-4 py-3">{contact.phone}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Location</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-md bg-gray-50 p-4 py-3">{contact.location || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Message Details */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        Message Details
                      </h3>
                      <div className="space-y-4 sm:space-y-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Subject</label>
                          <p className="text-sm sm:text-base md:text-lg text-gray-900 font-medium break-words border rounded-md bg-gray-50 p-4 py-3">{contact.subject}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Message</label>
                          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                            <p className="text-sm sm:text-base text-gray-900 leading-relaxed whitespace-pre-wrap break-words">
                              {contact.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Reply Information */}
                    {contact.reply && (
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                          Reply Information
                        </h3>
                        <div className="space-y-4 sm:space-y-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Reply Message</label>
                            <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-200">
                              <p className="text-sm sm:text-base text-gray-900 leading-relaxed whitespace-pre-wrap break-words">
                                {contact.reply}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Replied By</label>
                              <p className="text-sm sm:text-base text-gray-900 font-medium">{contact.replied_by_name || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Replied At</label>
                              <p className="text-sm sm:text-base text-gray-900 font-medium break-words">{formatDate(contact.replied_at)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Status</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-md bg-gray-50 p-4 py-3">
                            {contact.reply ? 'Replied' : 'Pending Reply'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Date Received</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-words border rounded-md bg-gray-50 p-4 py-3">{formatDate(contact.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Last Updated</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-words border rounded-md bg-gray-50 p-4 py-3">{formatDate(contact.updated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        Additional Information
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
                          <div>
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">About Contact Messages</h4>
                            <p className="text-xs sm:text-sm text-gray-700">
                              Contact messages are submitted by customers through the website contact form. 
                              Each message should be reviewed and replied to promptly to maintain good customer service.
                              {!contact.reply && ' This message is still pending a reply.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 flex flex-col sm:flex-row gap-3 sm:gap-4 border-t">
                    {!contact.reply && (
                      <button
                        onClick={() => navigate(`/contact/edit/${contactId}`)}
                        className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Send Reply</span>
                      </button>
                    )}
                    {contact.reply && (
                      <button
                        onClick={() => navigate(`/contact/edit/${contactId}`)}
                        className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <Eye
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">View Reply Details</span>
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/contact')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewContact;